/**
 * Camera math for the Plotly 3D viewer, kept pure so it can be unit-tested without a
 * browser or touch simulation (the gesture wiring lives in PlotlyVisualization).
 */

export interface CameraEye {
  x: number;
  y: number;
  z: number;
}

// How close/far the camera may get when pinch-zooming, as the length of the eye vector.
export const ZOOM_MIN_DISTANCE = 0.4;
export const ZOOM_MAX_DISTANCE = 8;

/**
 * Scale a camera eye vector for a pinch-zoom step.
 *
 * `ratio` is prevFingerDistance / currentFingerDistance: fingers spreading apart makes
 * the ratio < 1, pulling the camera closer (zoom in); pinching together makes it > 1,
 * pushing the camera back (zoom out). The resulting distance is clamped so the camera
 * can neither cross the scene nor fly arbitrarily far away.
 */
export function scaleEyeForZoom(
  eye: CameraEye,
  ratio: number,
  min = ZOOM_MIN_DISTANCE,
  max = ZOOM_MAX_DISTANCE
): CameraEye {
  const currentDistance = Math.hypot(eye.x, eye.y, eye.z);
  if (currentDistance <= 0) {
    return eye;
  }
  const targetDistance = Math.min(max, Math.max(min, currentDistance * ratio));
  const scale = targetDistance / currentDistance;
  return { x: eye.x * scale, y: eye.y * scale, z: eye.z * scale };
}
